import App from 'app';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import ApexChart from 'react-apexcharts';
import { GetProductCatalog, GetSalesSummaryByDate, GetSalesSummaryByDateByCatalog } from 'services/Product';
import { GetUser } from 'services/Session';
import { GetSetting } from 'services/Setting';

const formatDate = (date) => date.toISOString().slice(0, 10);

const groupData = (data, valueKey) => {
	const grouped = {};
	data.forEach(({ date, type, catalog_name }) => {
		const key = catalog_name ? `${type.replace('_', ' ')} - ${catalog_name}` : type.replace('_', ' ');
		if (!grouped[key]) grouped[key] = {};
		const value = data.find(d => d.date === date && d.type.replace('_', ' ') === type.replace('_', ' ') && d.catalog_name === catalog_name)?.[valueKey];
		if (typeof value === 'number') {
			grouped[key][date] = (grouped[key][date] || 0) + value;
		}
	});
	return grouped;
};

const prepareSeries = (grouped, dates, valueLabel = 'Rp') => {
	let series = Object.entries(grouped).map(([name, values]) => {
		const data = dates.map(date => (date in values ? values[date] : null));
		const total = data.reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
		return {
			name:
				valueLabel === 'Rp'
					? `${name}: ${valueLabel} ${total.toLocaleString()}`
					: `${name}: ${total.toLocaleString()} ${valueLabel}`,
			data,
			total, // tambahkan ini untuk sorting
		};
	});

	// Urutkan dari total tertinggi ke terendah
	series.sort((a, b) => b.total - a.total);

	// Hapus total dari objek (opsional)
	series = series.map(({ name, data }) => ({ name, data }));

	// Tambahkan total per tanggal sebagai seri terakhir (tidak perlu ikut diurutkan)
	const totalPerDate = dates.map(date =>
		Object.values(grouped).reduce((sum, values) => {
			const val = values[date];
			return sum + (typeof val === 'number' ? val : 0);
		}, 0)
	);
	const totalLine = {
		name: `total: ${valueLabel} ${totalPerDate.reduce((a, b) => a + b, 0).toLocaleString()}`,
		data: totalPerDate.map(v => (v > 0 ? v : null)),
	};

	series.push(totalLine);

	return series;
};


const extractDates = (data) => {
	const set = new Set(data.map(d => d.date));
	return Array.from(set).sort();
};

export function Chart() {
	const today = new Date();
	const sevenDaysAgo = new Date();
	sevenDaysAgo.setDate(today.getDate() - 7);
	const [startDate, setStartDate] = useState(formatDate(sevenDaysAgo));
	const [endDate, setEndDate] = useState(formatDate(today));

	const user = GetUser();
	const [setting, setSetting] = useState({});
	const [productCatalogs, setProductCatalogs] = useState([]);
	const [salesByDates, setSalesByDate] = useState([]);
	const [salesByDateByCatalogs, setSalesByDateByCatalogs] = useState([]);
	const [errorMessage, setErrorMessage] = useState('');

	const getData = async () => {
		await GetSetting(setSetting);
		try {
			const res = await GetProductCatalog({ 
				is_active: true,
				is_flat_response: true,
				type: 'ready_stock', 
				fields: 'name,quantity.available,unit_price.min,discount_rate', 
				'quantity.available.$gt': 0,
			});
			if (res?.data?.results) setProductCatalogs(res.data.results);
		} catch (error) {
			setErrorMessage(error?.response?.data?.error?.message ?? 'Gagal terhubung ke server.');
			setTimeout(() => setErrorMessage(''), 3000);
		}
		try {
			const res = await GetSalesSummaryByDate({ 'date.$gte': startDate, 'date.$lte': endDate });
			if (res?.data?.results) setSalesByDate(res.data.results);
		} catch (error) {
			setErrorMessage(error?.response?.data?.error?.message ?? 'Gagal terhubung ke server.');
			setTimeout(() => setErrorMessage(''), 3000);
		}
		try {
			const res = await GetSalesSummaryByDateByCatalog({ 'date.$gte': startDate, 'date.$lte': endDate });
			if (res?.data?.results) setSalesByDateByCatalogs(res.data.results);
		} catch (error) {
			setErrorMessage(error?.response?.data?.error?.message ?? 'Gagal terhubung ke server.');
			setTimeout(() => setErrorMessage(''), 3000);
		}
	};

	useEffect(() => {
		getData();
	}, [startDate, endDate]);

	const dates = extractDates([...salesByDates, ...salesByDateByCatalogs]);
	const typeGrouped = groupData(salesByDates, 'amount');
	const catalogGrouped = groupData(salesByDateByCatalogs, 'amount');
	const typeGroupedQty = groupData(salesByDates, 'quantity');
	const catalogGroupedQty = groupData(salesByDateByCatalogs, 'quantity');

	const commonOptions = useMemo(() => ({
		chart: { type: 'line', zoom: { enabled: false } },
		xaxis: { categories: dates, labels: { formatter: val => dayjs(val).format('D-MMM-YYYY') } },
		tooltip: {
			shared: true,
			intersect: false,
			hideEmptySeries: true,
			custom: function ({ series, seriesIndex, dataPointIndex, w }) {
				let tooltipHTML = `
			<div class="apexcharts-tooltip-title" style="font-weight: 600; padding: 4px 8px; border-bottom: 1px solid #e3e3e3;">
				${w.globals.categoryLabels[dataPointIndex]}
			</div>
		`;

				// Loop all series
				w.globals.seriesNames.forEach((fullName, idx) => {
					const value = series[idx][dataPointIndex];
					if (value == null) return; // skip null

					const color = w.globals.colors[idx] || '#000';
					const name = fullName.replace('_', ' ').split(':')[0]; // ambil nama sebelum `:`

					tooltipHTML += `
				<div style="display: flex; align-items: center; padding: 4px 8px;">
					<div style="width: 12px; height: 12px; background-color: ${color}; margin-right: 8px;"></div>
					<span style="flex: 1; margin-right: 8px;">${name}</span>
					<span style="font-weight: 500;">${fullName.includes(': Rp') ? 'Rp ' + value.toLocaleString() : value.toLocaleString() + ' Pcs'}</span>
				</div>
			`;
				});

				return tooltipHTML;
			}
		},
		stroke: { curve: 'smooth', width: 2 },
		legend: { position: 'right', horizontalAlign: 'top' }
	}), [dates]);

	const top10ProductCatalogs = productCatalogs.sort((a, b) => {
		const b_discount = b.discount_rate ?? 0;
		const a_discount = a.discount_rate ?? 0;

		const b_total = b["quantity.available"] * b["unit_price.min"] * (1 - b_discount / 100);
		const a_total = a["quantity.available"] * a["unit_price.min"] * (1 - a_discount / 100);

		return b_total - a_total; // Urutan dari besar ke kecil
	})

	const top10ProductCatalogsWithTotal = [
		...top10ProductCatalogs,
		{
			name: 'Total',
			discount_rate: 0,
			"quantity.available": 1,
			"unit_price.min": productCatalogs.reduce((sum, item) => {
				const qty = item["quantity.available"];
				const price = item["unit_price.min"];
				const discount = item.discount_rate ?? 0;
				return sum + qty * price * (1 - discount / 100);
			}, 0),
		}
	]

	return (
		<App user={user} setting={setting}>
			<div className="px-4 pb-4">
				{errorMessage && (
					<div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-6 py-3 rounded-md shadow-md flex items-center z-50">
						<span>{errorMessage}</span>
						<button className="ml-4 text-white focus:outline-none" onClick={() => setErrorMessage('')}>✖</button>
					</div>
				)}

				<div className="mx-auto mt-10 p-4 bg-white rounded-lg shadow-md w-full md:w-3/4">
					<div className="flex justify-center space-x-4 mb-6">
						<div>
							<label className="block text-sm font-medium text-center">Start Date</label>
							<input type="date" className="border rounded px-2 py-1" value={startDate} onChange={e => setStartDate(e.target.value)} />
						</div>
						<div>
							<label className="block text-sm font-medium text-center">End Date</label>
							<input type="date" className="border rounded px-2 py-1" value={endDate} onChange={e => setEndDate(e.target.value)} />
						</div>
					</div>

					<h2 className="text-lg font-semibold mb-2">Stok Tersedia</h2>
					<ApexChart
						options={{
							chart: {
								type: "bar",
							},
							xaxis: {
								categories: top10ProductCatalogsWithTotal.map((item) => item.name),
							},
							dataLabels: {
								enabled: false,
								formatter: (val) =>
									`Rp${val.toLocaleString("id-ID")}`,
							},
							yaxis: {
								labels: {
									formatter: (val) =>
										`Rp${val.toLocaleString("id-ID")}`,
								},
								title: {
									text: "Nilai Tersedia (Rp)",
								},
							},
							tooltip: {
								y: {
									formatter: (val) =>
										`Rp${val.toLocaleString("id-ID")}`,
								},
							},
						}}
						series={[{
							name: "Nilai Tersedia",
							data: top10ProductCatalogsWithTotal.map((item) => {
								const qty = item["quantity.available"];
								const price = item["unit_price.min"];
								const discount = item.discount_rate ?? 0;
								const total = qty * price * (1 - discount / 100);
								return Math.round(total);
							}),
						}]}
						type="bar"
						height={400}
					/>

					<h2 className="text-lg font-semibold mb-2">Penjualan Per Jenis Transaksi</h2>
					<ApexChart
						options={{
							...commonOptions,
							yaxis: { labels: { formatter: (val) => 'Rp ' + val?.toLocaleString?.() } },
						}}
						series={prepareSeries(typeGrouped, dates, 'Rp')}
						type="line"
						height={400}
					/>

					<h2 className="text-lg font-semibold mt-8 mb-2">Penjualan Per Jenis Produk</h2>
					<ApexChart
						options={{
							...commonOptions,
							yaxis: { labels: { formatter: (val) => 'Rp ' + val?.toLocaleString?.() } },
						}}
						series={prepareSeries(catalogGrouped, dates, 'Rp')}
						type="line"
						height={400}
					/>

					<h2 className="text-lg font-semibold mt-8 mb-2">Penjualan Per Jenis Transaksi (Qty)</h2>
					<ApexChart
						options={{
							...commonOptions,
							yaxis: { labels: { formatter: (val) => val?.toLocaleString?.() + ' Pcs' } },
						}}
						series={prepareSeries(typeGroupedQty, dates, 'Pcs')}
						type="line"
						height={400}
					/>

					<h2 className="text-lg font-semibold mt-8 mb-2">Penjualan Per Jenis Produk (Qty)</h2>
					<ApexChart
						options={{
							...commonOptions,
							yaxis: { labels: { formatter: (val) => val?.toLocaleString?.() + ' Pcs' } },
						}}
						series={prepareSeries(catalogGroupedQty, dates, 'Pcs')}
						type="line"
						height={400}
					/>
				</div>
			</div>
		</App>
	);
}